import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Optional, Self, Input, ContentChildren, ViewChild, HostBinding, NgModule } from '@angular/core';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i4 from '@angular/material/radio';
import { MatRadioButton, MatRadioGroup, MatRadioModule } from '@angular/material/radio';
import * as i1 from '@angular/forms';
import { ControlContainer, NgForm, FormsModule } from '@angular/forms';
import * as i2 from '@firestitch/label';
import { FsLabelModule } from '@firestitch/label';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

class FsRadioGroupComponent {
    constructor(_ngControl, _cdRef) {
        this._ngControl = _ngControl;
        this._cdRef = _cdRef;
        this.orientation = 'horizontal';
        this.radioPosition = 'top';
        this.compareWith = (o1, o2) => { return o1 === o2; };
        this.matRadioGroup = null;
        this.formWrapper = true;
        this._value = null;
        this._required = false;
        this._destroy$ = new Subject();
        this._onChange = (value) => { };
        this._ngControl.valueAccessor = this;
    }
    set required(value) {
        this._required = coerceBooleanProperty(value);
    }
    ngOnInit() {
        const control = this._ngControl.control;
        const validators = control.validator
            ? [control.validator, this.validate.bind(this)]
            : this.validate.bind(this);
        control.setValidators(validators);
        control.updateValueAndValidity();
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this._onChange(this._value);
    }
    ngAfterContentInit() {
        this.contentChildren.forEach((child) => {
            this._listenButtonChange(child);
        });
        this.contentChildren.changes
            .pipe(takeUntil(this._destroy$))
            .subscribe((children) => {
            children.forEach((child) => {
                this._listenButtonChange(child);
            });
            setTimeout(() => {
                this.updateChecked(this._value);
                this._cdRef.markForCheck();
                this._ngControl.control.updateValueAndValidity();
            });
        });
    }
    registerOnChange(fn) { this._onChange = fn; }
    registerOnTouched(fn) { }
    writeValue(value) {
        if (value !== undefined) {
            this.updateChecked(value);
            this._value = value;
        }
    }
    validate(control) {
        if (this._required) {
            const valueExists = this._verifyValueExists();
            if (!valueExists) {
                return {
                    required: true
                };
            }
        }
        return null;
    }
    updateChecked(value) {
        if (this.contentChildren) {
            for (const button of this.contentChildren.toArray()) {
                button.checked = this.compareWith(button.value, value);
            }
        }
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.contentChildren.forEach((btn) => {
            btn.disabled = this.disabled;
        });
    }
    _listenButtonChange(button) {
        button.name = this.name;
        button.change
            .pipe(takeUntil(this._destroy$))
            .subscribe((event) => {
            if (!button.disabled) {
                this.value = button.value;
            }
        });
    }
    _verifyValueExists() {
        return Array.from(this.contentChildren || [])
            .some((child) => {
            return this.compareWith(this.value, child.value);
        });
    }
}
FsRadioGroupComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsRadioGroupComponent, deps: [{ token: i1.NgControl, optional: true, self: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsRadioGroupComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.16", type: FsRadioGroupComponent, selector: "fs-radio-group", inputs: { orientation: "orientation", label: "label", name: "name", disabled: "disabled", radioPosition: "radioPosition", compareWith: "compareWith", required: "required" }, host: { properties: { "class.fs-form-wrapper": "this.formWrapper" } }, providers: [], queries: [{ propertyName: "contentChildren", predicate: MatRadioButton, descendants: true }], viewQueries: [{ propertyName: "matRadioGroup", first: true, predicate: MatRadioGroup, descendants: true, static: true }], ngImport: i0, template: "<fs-label-field>\n  <fs-label *ngIf=\"label\" class=\"fs-form-label\">{{ label }}</fs-label>\n  <mat-radio-group\n    [ngClass]=\"[orientation, 'position-' + radioPosition ]\"\n    [disabled]=\"disabled\">\n    <ng-content></ng-content>\n  </mat-radio-group>\n  <fs-label-message>\n    <div class=\"fs-form-message\">\n      <div class=\"fs-form-hint\">\n        <ng-content select=\"mat-hint\"></ng-content>\n      </div>\n    </div>\n  </fs-label-message>\n</fs-label-field>\n", styles: [":host{position:relative}:host ::ng-deep .mat-radio-group .mat-radio-label{white-space:normal;min-height:22px}:host ::ng-deep .mat-radio-group .mat-radio-label-content{line-height:22px}:host ::ng-deep .mat-radio-group:not(.vertical) .mat-radio-button{margin-right:15px}:host ::ng-deep .mat-radio-group:not(.vertical) .mat-radio-button:last-child{margin-right:0}:host ::ng-deep .mat-radio-group.vertical .mat-radio-button{display:block;margin:10px 0}:host ::ng-deep .mat-radio-group.vertical .mat-radio-button:first-child{margin-top:0}:host ::ng-deep .mat-radio-group.vertical .mat-radio-button:last-child{margin-bottom:0}:host ::ng-deep .position-top .mat-radio-label{align-items:flex-start}\n"], components: [{ type: i2.FsLabelFieldComponent, selector: "fs-label-field", inputs: ["bottomMargin", "topMargin", "labelMargin"] }, { type: i2.FsLabelComponent, selector: "fs-label" }, { type: i2.FsLabelMessageComponent, selector: "fs-label-message" }], directives: [{ type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.MatRadioGroup, selector: "mat-radio-group", exportAs: ["matRadioGroup"] }, { type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }], viewProviders: [{ provide: ControlContainer, useExisting: NgForm }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsRadioGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-radio-group',
                    templateUrl: './radio-group.component.html',
                    styleUrls: ['radio-group.component.scss'],
                    providers: [],
                    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i1.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { orientation: [{
                type: Input
            }], label: [{
                type: Input
            }], name: [{
                type: Input
            }], disabled: [{
                type: Input
            }], radioPosition: [{
                type: Input
            }], compareWith: [{
                type: Input
            }], required: [{
                type: Input
            }], contentChildren: [{
                type: ContentChildren,
                args: [MatRadioButton, { descendants: true }]
            }], matRadioGroup: [{
                type: ViewChild,
                args: [MatRadioGroup, { static: true }]
            }], formWrapper: [{
                type: HostBinding,
                args: ['class.fs-form-wrapper']
            }] } });

class FsRadioGroupModule {
}
FsRadioGroupModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsRadioGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsRadioGroupModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsRadioGroupModule, declarations: [FsRadioGroupComponent], imports: [CommonModule,
        MatRadioModule,
        FormsModule,
        FsLabelModule], exports: [FsRadioGroupComponent] });
FsRadioGroupModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsRadioGroupModule, imports: [[
            CommonModule,
            MatRadioModule,
            FormsModule,
            FsLabelModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsRadioGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        MatRadioModule,
                        FormsModule,
                        FsLabelModule
                    ],
                    declarations: [
                        FsRadioGroupComponent
                    ],
                    exports: [
                        FsRadioGroupComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FsRadioGroupComponent, FsRadioGroupModule };
